/**
 * FreeAgent OAuth2 Callback Page
 * 
 * Handles the redirect from FreeAgent after user authorization
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Card, Typography, CircularProgress, Alert } from '@mui/joy';
import { handleCallback } from '../freeagent/auth';

export const FreeAgentCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setStatus('error');
        setError(errorDescription || errorParam || 'Authorization failed');
        return;
      }

      if (!code) {
        setStatus('error');
        setError('No authorization code received');
        return;
      }

      try {
        const result = await handleCallback(code);
        
        if (result.success) {
          setStatus('success');
          // Redirect to settings or dashboard after 2 seconds
          setTimeout(() => {
            navigate('/settings?tab=freeagent', { replace: true });
          }, 2000);
        } else {
          setStatus('error');
          setError(result.error || 'Failed to complete authorization');
        }
      } catch (err: any) {
        setStatus('error');
        setError(err.message || 'An unexpected error occurred');
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      p: 2
    }}>
      <Card sx={{ maxWidth: 500, width: '100%', textAlign: 'center' }}>
        {status === 'processing' && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography level="h4">Connecting to FreeAgent...</Typography>
            <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
              Please wait while we complete the authorization.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Typography level="h4" color="success" sx={{ mb: 2 }}>
              ✓ Successfully Connected!
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              You have successfully connected your FreeAgent account.
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Redirecting to settings...
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert color="danger" sx={{ mb: 2 }}>
              <Typography level="h4">Connection Failed</Typography>
              <Typography level="body-sm" sx={{ mt: 1 }}>
                {error || 'An error occurred during authorization'}
              </Typography>
            </Alert>
            <button
              onClick={() => navigate('/settings?tab=freeagent')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#0b6bcb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go to Settings
            </button>
          </>
        )}
      </Card>
    </Box>
  );
};

